/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as yup from 'yup'
import moment from 'moment'

import { t } from '@/i18n'
import { IDeal } from '@/types'
import { useForm } from '@/hooks/index'
import {
  Accordion,
  DatePicker,
  Form,
  FormButtons,
  OptionSelector,
  TextArea,
  TextInput,
} from '@/components/index'
import CarSubModelSelector from '@/pages/Car/carSubModelSelector'
import CustomerSelector from '@/pages/Customer/customerSelector'
import ProposalSelector from '@/pages/Proposal/proposalSelector'
import UserSelector from '@/pages/User/userSelector'
import CarSelector from '@/pages/Car/carSelector'
import { getDealEnabledDates } from '@/utils'
import ProposalPriceFormComponents from '@/pages/Proposal/FormComponents/ProposalPriceFormComponents'
import { useSelect } from './useSelect'
import { DealsDealStatusChoices } from '@/apollo/graphql'

interface Props {
  isEditing?: boolean
  initialData?: Partial<IDeal>
  loading: boolean
  submitting: boolean
  onCancel: () => void
  onSubmit: (d: IDeal, cb?: () => void) => void
}

const DealForm: React.FC<Props> = ({
  isEditing,
  initialData,
  loading,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const [enabledDates, setEnabledDates] = useState<
    Record<'startDate' | 'endDate', Date[] | undefined>
  >({ startDate: undefined, endDate: undefined })

  const schema = useMemo(
    () =>
      yup
        .object({
          leaseDuration: yup
            .number()
            .required(t('required_field_error'))
            .max(2147483647),
          status: yup
            .mixed<DealsDealStatusChoices>()
            .oneOf(
              Object.values(DealsDealStatusChoices),
              t('required_field_error'),
            ),
          startDate: yup
            .date()
            .required(t('required_field_error'))
            .typeError(t('invalid_date_error')),
          endDate: yup
            .date()
            .required(t('required_field_error'))
            .typeError(t('invalid_date_error')),
          npv: yup
            .number()
            .nullable()
            .transform((value, originalValue) =>
              originalValue === '' ||
              originalValue === null ||
              originalValue === undefined
                ? null
                : value,
            ),
        })
        .required(),
    [],
  )

  const formRef = useRef<HTMLFormElement | null>(null)

  const { control, isValid, getValues, onTriggerChange, onFormSubmit, watch } =
    useForm<any>({
      isEditing,
      defaultValues: initialData,
      schema,
      onSubmit,
    })

  const { dealStatusOptions } = useSelect()
  const startDate = watch('startDate')

  useEffect(() => {
    const _sDate = initialData?.startDate || new Date()
    const _eDate = initialData?.endDate || new Date()

    const _start = getDealEnabledDates(_sDate)
    const _end = getDealEnabledDates(_eDate)
    setEnabledDates({
      startDate: _start,
      endDate: _end,
    })
  }, [initialData?.endDate, initialData?.startDate])

  useEffect(() => {
    onTriggerChange('customer', initialData?.customer?.id)
  }, [initialData?.customer?.id, onTriggerChange])

  useEffect(() => {
    onTriggerChange('proposal', initialData?.proposal?.id)
  }, [initialData?.proposal?.id, onTriggerChange])

  useEffect(() => {
    onTriggerChange('car', initialData?.car?.id)
  }, [initialData?.car?.id, onTriggerChange])

  useEffect(() => {
    onTriggerChange('agent', initialData?.agent?.id)
  }, [initialData?.agent?.id, onTriggerChange])

  useEffect(() => {
    onTriggerChange('carBrand', initialData?.carSubModel?.carModel?.brand?.id)
  }, [onTriggerChange, initialData?.carSubModel?.carModel?.brand?.id])

  useEffect(() => {
    onTriggerChange('carModel', initialData?.carSubModel?.carModel?.id)
  }, [onTriggerChange, initialData?.carSubModel?.carModel?.id])

  useEffect(() => {
    onTriggerChange('carSubModel', initialData?.carSubModel?.id)
  }, [onTriggerChange, initialData])

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])
  const onSyncFields = useCallback(
    (f: string, v: any) => {
      if (f === 'startDate') {
        const endDate = getValues('endDate')
        if (endDate) {
          onTriggerChange(
            'leaseDuration',
            moment(endDate, 'YYYY-MM-DD').diff(v, 'months'),
          )
        }
      } else if (f === 'endDate') {
        const startDate = getValues('startDate')
        if (startDate) {
          onTriggerChange(
            'leaseDuration',
            moment(v).diff(moment(startDate, 'YYYY-MM-DD'), 'months'),
          )
        }
      } else if (!isNaN(+v) && f === 'leaseDuration') {
        const startDate = getValues('startDate')
        if (startDate) {
          onTriggerChange(
            'endDate',
            moment(startDate, 'YYYY-MM-DD')
              .add(+v, 'months')
              .format('YYYY-MM-DD'),
          )
        }
      }
    },
    [getValues, onTriggerChange],
  )

  const onChangeMonth = useCallback(
    (d: Date, field: 'startDate' | 'endDate') => {
      const _dates = getDealEnabledDates(d)
      const newData =
        field === 'startDate'
          ? { ...enabledDates, startDate: _dates }
          : { ...enabledDates, endDate: _dates }
      setEnabledDates(newData)
    },
    [enabledDates],
  )

  useEffect(() => {
    if (startDate) {
      const v = getValues('leaseDuration')
      if (!isNaN(+v)) {
        onTriggerChange(
          'endDate',
          moment(startDate, 'YYYY-MM-DD')
            .add(+v, 'months')
            .format('YYYY-MM-DD'),
        )
      }
    }
  }, [startDate, getValues])

  return (
    <div className="edit-info form-fields">
      <div className="data-box">
        {loading ? (
          <div className="loading-text">{t('loading')}</div>
        ) : (
          <>
            <Form<IDeal>
              className={isEditing ? 'editing' : 'view'}
              isEditing={isEditing}
              formRef={formRef}
              control={control}
              onSubmit={onFormSubmit}
            >
              <Accordion preExpanded isFormElement title={t('general_info')}>
                {(props: any) => (
                  <>
                    <DatePicker
                      {...props}
                      required
                      className="w-33"
                      name="startDate"
                      dateFormat="yyyy-MM-dd"
                      disabled={!Boolean(isEditing)}
                      label={t('start_date')}
                      includeDates={enabledDates.startDate}
                      onMonthChange={(d: Date) => onChangeMonth(d, 'startDate')}
                      onFieldChange={d => onSyncFields('startDate', d)}
                    />
                    <DatePicker
                      {...props}
                      required
                      name="endDate"
                      className="w-33"
                      dateFormat="yyyy-MM-dd"
                      emptyValue={emptyValue}
                      disabled={!Boolean(isEditing)}
                      label={t('end_date')}
                      includeDates={enabledDates.endDate}
                      onMonthChange={(d: Date) => onChangeMonth(d, 'endDate')}
                      onFieldChange={d => onSyncFields('endDate', d)}
                    />
                    <TextInput
                      {...props}
                      required
                      name="leaseDuration"
                      type="number"
                      min={0}
                      className="w-33"
                      emptyValue={emptyValue}
                      disabled={!Boolean(isEditing)}
                      label={t('lease_duration')}
                      onFieldChange={d => onSyncFields('leaseDuration', d)}
                    />
                    <OptionSelector
                      {...props}
                      required
                      name="status"
                      className="w-33"
                      disabled={!Boolean(isEditing)}
                      options={dealStatusOptions}
                      emptyValue={emptyValue}
                      label={t('status')}
                    />
                    <ProposalSelector
                      {...props}
                      isFormElement
                      className="w-33"
                      isEditing={isEditing}
                      initial={initialData?.customer}
                      selected={getValues('proposal')}
                    />
                    <CustomerSelector
                      {...props}
                      isFormElement
                      className="w-33"
                      isEditing={isEditing}
                      initial={initialData?.customer}
                      selected={getValues('customer')}
                    />
                    <TextInput
                      {...props}
                      name="isOperational"
                      type="checkbox"
                      emptyValue={emptyValue}
                      disabled={!Boolean(isEditing)}
                      label={t('is_operational')}
                    />
                  </>
                )}
              </Accordion>
              <Accordion preExpanded isFormElement title={t('price_info')}>
                {(props: any) => (
                  <>
                    {!isEditing && (
                      <TextInput
                        {...props}
                        isCurrency
                        name="fullMonthlyPrice"
                        type="number"
                        min={0}
                        emptyValue={emptyValue}
                        disabled={!Boolean(isEditing)}
                        label={t('full_monthly_payment')}
                      />
                    )}
                    <ProposalPriceFormComponents
                      {...props}
                      initialData={initialData}
                      onTriggerChange={onTriggerChange}
                      isFormElement
                      emptyValue={emptyValue}
                      getValues={getValues}
                      watch={watch}
                      isEditing={isEditing}
                    />
                    <TextInput
                      {...props}
                      name="npv"
                      type="number"
                      emptyValue={emptyValue}
                      disabled={!Boolean(isEditing)}
                      label={t('npv')}
                    />
                  </>
                )}
              </Accordion>
              <Accordion preExpanded isFormElement title={t('car_details')}>
                {(props: any) => (
                  <>
                    <CarSelector
                      {...props}
                      isFormElement
                      isEditing={isEditing}
                      initial={initialData?.car}
                      selected={getValues('car')}
                    />

                    <CarSubModelSelector
                      {...props}
                      isFormElement
                      loading={loading}
                      isEditing={isEditing}
                      initialBrand={initialData?.carSubModel?.carModel?.brand}
                      initialModel={initialData?.carSubModel?.carModel}
                      initialSubModel={initialData?.carSubModel}
                      onTriggerChange={onTriggerChange}
                    />

                    <TextInput
                      {...props}
                      name="modelYear"
                      type="number"
                      min={0}
                      emptyValue={emptyValue}
                      disabled={!Boolean(isEditing)}
                      label={t('model_year')}
                    />
                    <TextInput
                      {...props}
                      name="freeKmPerYear"
                      type="number"
                      min={0}
                      emptyValue={emptyValue}
                      disabled={!Boolean(isEditing)}
                      label={t('free_km_per_year')}
                    />
                  </>
                )}
              </Accordion>
              <UserSelector
                isFormElement
                name="agent"
                label={t('agent')}
                isEditing={isEditing}
                initial={initialData?.agent}
              />
              <TextArea
                className={isEditing ? 'full' : ''}
                name="notes"
                label={t('notes')}
                rows={3}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
              />
            </Form>
            <FormButtons
              visible={isEditing}
              formRef={formRef}
              isFormValid={isValid}
              submitting={submitting}
              onCancel={onCancel}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default DealForm
